import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../../api/PrivateAdminPanelInstance";
import { getAccessToken } from "../../api/getAccessToken";

export const SGP_RISK_LIST_QUERY_KEY = "SGP_RISK_LIST" as const;
export const SGP_RISK_SPORTS_QUERY_KEY = "SGP_RISK_SPORTS" as const;

type Nullable<T> = T | null | undefined;

export type SgpRiskListParams = {
  start_date?: Nullable<string>;
  end_date?: Nullable<string>;
  page: number;
  page_size: number;
  search_term?: Nullable<string>;
  min_legs?: Nullable<number>;
  sport_ids?: Nullable<string[]>;
  min_settled_payout?: Nullable<number>;
  min_pl?: Nullable<number>;
  min_roi?: Nullable<number>;
  sort_by?:
    | "sgp_id"
    | "sport"
    | "league_name"
    | "total_wagered"
    | "pending_volume"
    | "settled_volume"
    | "settled_payout"
    | "pl"
    | "roi"
    | "current_exposure"
    | "exposure_limit"
    | "exposure_used_percentage";
  sort_order?: "asc" | "desc";
};

export type SgpRiskApiRow = {
  sgp_id: string;
  sport: string;
  sport_id: string;
  league_id?: string | null;
  league_name?: string | null;
  total_wagered: number;
  pending_volume: number;
  settled_volume: number;
  settled_payout: number;
  pl: number;
  roi: number;
  current_exposure: number;
  exposure_limit: number;
  exposure_limit_source?: string | null;
  exposure_used_percentage: number;
  sgp_adjustment: number | null;
  exposure_controls_editable?: boolean;
};

export type SgpRiskTableRow = {
  id: string;
  sgpId: string;
  sport: string;
  league: string;
  leagueId: string;
  sportId: string;
  totalWagered: string;
  pendingVolume: string;
  settledVolume: string;
  settledPayout: string;
  pl: { text: string; positive: boolean };
  roi: string;
  pendingExp: string;
  expLimitPct: number;
  pendingExpLimit: string;
  exposureLimitSource: string;
  exposureControlsEditable: boolean;
  sgpAdjustment: string;
};

export type SgpRiskSportOption = {
  id: string;
  title: string;
};

export type SgpRiskListResult = {
  rows: SgpRiskTableRow[];
  pagination: {
    current_page: number;
    page_size: number;
    total_results: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
  applied_date_range: {
    start: string;
    end: string;
  } | null;
  data_warnings: string[];
};

export type SgpRiskExposureControlsPayload = {
  sgp_id: string;
  sport_id: string;
  league_id: string | null;
  exposure_limit?: number | null;
  sgp_adjustment?: number | null;
};

const EMPTY_PAGINATION = {
  current_page: 1,
  page_size: 10,
  total_results: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
};

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactCurrencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

function finiteNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function optionalNumber(value: Nullable<number>): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function optionalString(value: Nullable<string>): string | null {
  const trimmed = typeof value === "string" ? value.trim() : "";
  return trimmed.length > 0 ? trimmed : null;
}

function makeRowId(row: SgpRiskApiRow): string {
  return `${row.sgp_id}::${row.sport_id}::${row.league_id ?? ""}`;
}

function normalizeAppliedDateRange(
  value: unknown,
): SgpRiskListResult["applied_date_range"] {
  if (!value || typeof value !== "object") {
    return null;
  }

  const dateRange = value as { start?: unknown; end?: unknown };
  return typeof dateRange.start === "string" && typeof dateRange.end === "string"
    ? { start: dateRange.start, end: dateRange.end }
    : null;
}

export function mapSgpRiskApiRowToTableRow(row: SgpRiskApiRow): SgpRiskTableRow {
  const pl = finiteNumber(row.pl);
  const roi = finiteNumber(row.roi);
  return {
    id: makeRowId(row),
    sgpId: String(row.sgp_id ?? ""),
    sport: String(row.sport ?? ""),
    league: String(row.league_name ?? ""),
    leagueId: String(row.league_id ?? ""),
    sportId: String(row.sport_id ?? ""),
    totalWagered: currencyFormatter.format(finiteNumber(row.total_wagered)),
    pendingVolume: currencyFormatter.format(finiteNumber(row.pending_volume)),
    settledVolume: currencyFormatter.format(finiteNumber(row.settled_volume)),
    settledPayout: currencyFormatter.format(finiteNumber(row.settled_payout)),
    pl: {
      text: compactCurrencyFormatter.format(pl),
      positive: pl >= 0,
    },
    roi: `${roi.toFixed(2)}%`,
    pendingExp: currencyFormatter.format(finiteNumber(row.current_exposure)),
    expLimitPct: finiteNumber(row.exposure_used_percentage),
    pendingExpLimit: compactCurrencyFormatter.format(finiteNumber(row.exposure_limit)),
    exposureLimitSource: String(row.exposure_limit_source ?? ""),
    exposureControlsEditable: row.exposure_controls_editable !== false,
    sgpAdjustment:
      row.sgp_adjustment == null ? "" : String(finiteNumber(row.sgp_adjustment)),
  };
}

function normalizeListResponse(body: unknown): SgpRiskListResult {
  const root = body as { data?: Record<string, unknown> } | null | undefined;
  const data = root?.data ?? {};
  const apiRows = Array.isArray(data.rows) ? (data.rows as SgpRiskApiRow[]) : [];
  const pagination =
    data.pagination && typeof data.pagination === "object"
      ? { ...EMPTY_PAGINATION, ...(data.pagination as object) }
      : EMPTY_PAGINATION;

  return {
    rows: apiRows.map(mapSgpRiskApiRowToTableRow),
    pagination,
    applied_date_range: normalizeAppliedDateRange(data.applied_date_range),
    data_warnings: Array.isArray(data.data_warnings)
      ? (data.data_warnings as string[])
      : [],
  };
}

function buildListRequestBody(params: SgpRiskListParams) {
  return {
    start_date: optionalString(params.start_date),
    end_date: optionalString(params.end_date),
    page: params.page,
    page_size: params.page_size,
    search_term: optionalString(params.search_term),
    min_legs: optionalNumber(params.min_legs),
    sport_ids: params.sport_ids?.length ? params.sport_ids : null,
    min_settled_payout: optionalNumber(params.min_settled_payout),
    min_pl: optionalNumber(params.min_pl),
    min_roi: optionalNumber(params.min_roi),
    sort_by: params.sort_by ?? "exposure_used_percentage",
    sort_order: params.sort_order ?? "desc",
  };
}

export async function fetchSgpRiskList(
  params: SgpRiskListParams,
  signal?: AbortSignal,
): Promise<SgpRiskListResult> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.post(
    API_ROUTES.GET_SGP_RISK_LIST,
    buildListRequestBody(params),
    { signal },
  );
  return normalizeListResponse(response?.data);
}

export async function fetchSgpRiskSports(): Promise<SgpRiskSportOption[]> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  const response = await instance.get(API_ROUTES.GET_SGP_RISK_SPORTS_DROPDOWN);
  const data = response?.data?.data;
  return Array.isArray(data) ? (data as SgpRiskSportOption[]) : [];
}

export async function updateSgpRiskExposureControls(
  payload: SgpRiskExposureControlsPayload,
): Promise<void> {
  const instance = createPrivateAdminPanelInstance(getAccessToken());
  // POST per Phase 1 API contract; PATCH is not supported yet.
  await instance.post(API_ROUTES.UPDATE_SGP_RISK_EXPOSURE_CONTROLS, payload);
}

function numberToken(value: Nullable<number>): string {
  return typeof value === "number" && Number.isFinite(value) ? String(value) : "";
}

export const SGP_RISK_LIST_PAGE_QUERY_KEY_INDEX = 3;

export function resolveSgpRiskListPlaceholderData<T>(
  previousData: T | undefined,
  previousQuery: { queryKey: readonly unknown[] } | undefined,
  nextQueryKey: readonly unknown[],
): T | undefined {
  if (!previousData || !previousQuery) {
    return undefined;
  }

  const previousKey = previousQuery.queryKey;
  const onlyPageChanged = nextQueryKey.every((value, index) => {
    if (index === SGP_RISK_LIST_PAGE_QUERY_KEY_INDEX) {
      return true;
    }

    return previousKey[index] === value;
  });

  return onlyPageChanged ? keepPreviousData(previousData) : undefined;
}

export function useSgpRiskList(params: SgpRiskListParams) {
  const {
    start_date,
    end_date,
    page,
    page_size,
    search_term,
    min_legs,
    sport_ids,
    min_settled_payout,
    min_pl,
    min_roi,
    sort_by,
    sort_order,
  } = params;

  const queryKey = [
    SGP_RISK_LIST_QUERY_KEY,
    start_date ?? "",
    end_date ?? "",
    page,
    page_size,
    search_term ?? "",
    numberToken(min_legs),
    sport_ids?.join(",") ?? "",
    numberToken(min_settled_payout),
    numberToken(min_pl),
    numberToken(min_roi),
    sort_by ?? "exposure_used_percentage",
    sort_order ?? "desc",
  ] as const;

  return useQuery<SgpRiskListResult>({
    queryKey,
    queryFn: ({ signal }) => fetchSgpRiskList(params, signal),
    enabled: Boolean(start_date && end_date),
    placeholderData: (previousData, previousQuery) =>
      resolveSgpRiskListPlaceholderData(previousData, previousQuery, queryKey),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
}

export function useSgpRiskSports() {
  return useQuery<SgpRiskSportOption[]>({
    queryKey: [SGP_RISK_SPORTS_QUERY_KEY],
    queryFn: fetchSgpRiskSports,
    staleTime: 1000 * 60 * 10,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateSgpRiskExposureControls() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSgpRiskExposureControls,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [SGP_RISK_LIST_QUERY_KEY] });
    },
  });
}
